import { nextArrivalDwell } from "./tier3Arrival";

export function freshReturnLocation(device, nowMs) {
  if (!device || device.status !== "active"
      || !Number.isFinite(device.latitude) || Math.abs(device.latitude) > 90
      || !Number.isFinite(device.longitude) || Math.abs(device.longitude) > 180
      || !Number.isFinite(device.accuracy) || device.accuracy < 0 || device.accuracy > 100
      || !Number.isFinite(device.requestedAt)
      || nowMs - device.requestedAt < -5000 || nowMs - device.requestedAt > 30000) return null;
  return { latitude: device.latitude, longitude: device.longitude,
    accuracy_metres: device.accuracy, captured_at: new Date(device.requestedAt).toISOString() };
}

// One controller belongs to one mounted, active navigation session. Cancel
// invalidates replies even if the HTTP transport cannot abort in time.
export function createPunctualReturnController({ lessonId, startedAt, request, onReturn, onError, onArrival,
  now = Date.now }) {
  let cancelled = false;
  let busy = false;
  let nextCheck = 0;
  let published = null;
  let arrival = null;
  let arrived = false;
  let lastArrivalFix = null;
  let latestDevice = null;
  const abort = new AbortController();
  return {
    cancel() { cancelled = true; abort.abort(); },
    async tick(device, savedPlan) {
      latestDevice = device;
      if (cancelled) return;
      const time = now();
      const location = freshReturnLocation(device, time);
      if (!location) {
        arrival = null;
        onError("Waiting for a fresh location to check the return journey.");
        return;
      }
      // A repeated last fix cannot prove a stationary dwell. Require two
      // distinct measured fixes, using their capture times rather than a timer.
      if (savedPlan?.return_mode && !arrived && device.requestedAt !== lastArrivalFix) {
        const destination = savedPlan.legs?.at(-1)?.destination_point;
        if (lastArrivalFix != null && (device.requestedAt <= lastArrivalFix || device.requestedAt - lastArrivalFix > 15000)) arrival = null;
        lastArrivalFix = device.requestedAt;
        // A coarse fix can price the return but cannot prove arrival inside
        // the 35-metre drop-off radius.
        const arrivalDevice = device.accuracy <= 35 ? device : { ...device, speed: null };
        arrival = nextArrivalDwell(arrival, { device: arrivalDevice, destination, nowMs: device.requestedAt });
        if (arrival.ready) { arrived = true; onArrival(); return; }
      }
      if (arrived || busy || time < nextCheck) return;
      busy = true;
      nextCheck = time + 60000;
      try {
        const data = await request(location, abort.signal);
        if (cancelled || !freshReturnLocation(latestDevice, now())) return;
        if (data?.lesson_id !== lessonId || data?.started_at !== startedAt
            || !["TEACHING", "RETURNING"].includes(data?.status)
            || !Number.isFinite(Date.parse(data.latest_safe_departure_iso))) throw new Error("Return check could not be confirmed.");
        if (data.status === "RETURNING") {
          const plan = data.route_plan;
          const saved = data.saved_plan;
          if (!plan?.route_geometry || plan.route_state !== "READY" || !plan.return_mode
              || !saved?.return_mode || !saved.navigation_recipe || !saved.recovery_id
              || saved.recovery_id !== plan.recovery_id
              || saved.challenge_sequence?.length !== 0) throw new Error("Return guidance could not be confirmed.");
          if (published !== saved.recovery_id) {
            published = saved.recovery_id;
            onReturn(data);
          }
        } else {
          // Recheck at the leave-by boundary; never switch using an old route.
          nextCheck = Math.min(nextCheck, Math.max(now() + 1000, Date.parse(data.latest_safe_departure_iso)));
        }
        onError("");
      } catch (error) {
        if (!cancelled) onError("Return timing could not be checked. Current guidance has been kept.");
      } finally { busy = false; }
    },
  };
}
